import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PhoneIcon, CalendarIcon, UsersIcon, TrendingUpIcon, LogOutIcon } from 'lucide-react';
export function Dashboard() {
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const stats = [{
    label: 'Total Calls',
    value: '1,234',
    icon: PhoneIcon,
    color: 'from-indigo-500 to-purple-500'
  }, {
    label: 'Appointments',
    value: '89',
    icon: CalendarIcon,
    color: 'from-sky-500 to-blue-500'
  }, {
    label: 'Leads Captured',
    value: '156',
    icon: UsersIcon,
    color: 'from-green-500 to-emerald-500'
  }, {
    label: 'Success Rate',
    value: '94%',
    icon: TrendingUpIcon,
    color: 'from-orange-500 to-red-500'
  }];
  return <div className="min-h-screen bg-gray-50" data-id="element-217">
      <header className="bg-white border-b border-gray-200" data-id="element-218">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8" data-id="element-219">
          <div className="flex items-center justify-between h-16 md:h-20" data-id="element-220">
            <div className="text-xl md:text-2xl font-bold text-black" data-id="element-221">
              WellAd<span className="text-indigo-600" data-id="element-222">.</span>
            </div>
            <div className="flex items-center space-x-4" data-id="element-223">
              <div className="hidden md:block text-right" data-id="element-224">
                <p className="text-sm font-medium text-black" data-id="element-225">{user?.name}</p>
                <p className="text-xs text-gray-600" data-id="element-226">{user?.email}</p>
              </div>
              <button onClick={handleLogout} className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:text-black transition-colors" data-id="element-227">
                <LogOutIcon size={18} data-id="element-228" />
                <span className="hidden md:inline" data-id="element-229">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12" data-id="element-230">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-231">
          <h1 className="text-2xl md:text-3xl font-bold text-black mb-2" data-id="element-232">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-233">
            Here's what's happening with your AI receptionist
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-234">
            {stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-235">
                <div className="flex items-center justify-between mb-4" data-id="element-236">
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center`} data-id="element-237">
                    <stat.icon size={24} className="text-white" data-id="element-238" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-black mb-1" data-id="element-239">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-600" data-id="element-240">{stat.label}</p>
              </motion.div>)}
          </div>
          <div className="grid lg:grid-cols-2 gap-6" data-id="element-241">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-242">
              <h3 className="text-lg font-bold text-black mb-4" data-id="element-243">
                Recent Activity
              </h3>
              <div className="space-y-4" data-id="element-244">
                {[{
                time: '2 minutes ago',
                text: 'Call answered from +1 234 567 8900'
              }, {
                time: '15 minutes ago',
                text: 'Appointment scheduled for tomorrow'
              }, {
                time: '1 hour ago',
                text: 'New lead captured: Sarah Johnson'
              }, {
                time: '2 hours ago',
                text: 'Call answered from +1 234 567 8901'
              }].map((activity, index) => <div key={index} className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-0" data-id="element-245">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2" data-id="element-246" />
                    <div className="flex-1" data-id="element-247">
                      <p className="text-sm text-black" data-id="element-248">{activity.text}</p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-249">
                        {activity.time}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-250">
              <h3 className="text-lg font-bold text-black mb-4" data-id="element-251">
                Quick Actions
              </h3>
              <div className="space-y-3" data-id="element-252">
                <button className="w-full px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors text-left" data-id="element-253">
                  Configure AI Settings
                </button>
                <button className="w-full px-4 py-3 bg-gray-100 text-black rounded-lg font-medium hover:bg-gray-200 transition-colors text-left" data-id="element-254">
                  View Call History
                </button>
                <button className="w-full px-4 py-3 bg-gray-100 text-black rounded-lg font-medium hover:bg-gray-200 transition-colors text-left" data-id="element-255">
                  Manage Integrations
                </button>
                <button className="w-full px-4 py-3 bg-gray-100 text-black rounded-lg font-medium hover:bg-gray-200 transition-colors text-left" data-id="element-256">
                  Download Reports
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </main>
    </div>;
}